"use client";

//region Imports
import React, {useEffect, useMemo, useRef, useState, useCallback} from "react";
import {useForm} from "react-hook-form";
import _ from "lodash";

import {$store, $e} from "@isudio/globals";
import {pages, policies} from "@isudio/config";
import {$core} from "@isudio/core";
import {useMergeRefs} from "@isudio/utils";
import {UserStatus} from "@isudio/data";
import type {IUserSignupResponse, IUser, IUserSignin, ISigninPayload, IUserSigninResponse} from "@isudio/data";
import {ToolbarTop, ToolbarBottom, LoadingSpinner, InputValidator, Label, RadioGroup, RadioGroupItem, ChangePassword, useDialogController, useTippyRegistry} from "../";
import type {HeaderElement, FooterElement, BaseDialogProps} from "../";
//endregion Imports

//region Types & Interfaces
interface SigninProps extends BaseDialogProps {
  dialogOptions?: any;
  user?: IUserSignupResponse | IUser;
  onSuccess?: () => void;
  onClose?: () => void;
}
//endregion Types & Interfaces

export function Signin({
                         dialogOptions,
                         user,
                         onSuccess,
                         onClose
                       }: SigninProps) {
  //region Variables
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [footerHeight, setFooterHeight] = useState(0);
  const [isOverflow, setIsOverflow] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const showFooter = !isOverflow || isAtBottom;
  const {name} = dialogOptions;
  const className = name.toLowerCase().replace(/ /g, "-");

  const [method, setMethod] = useState("id");
  const defaultProfilePic = _.get(pages.signup.mediaPicker, _.isDark() ? "defaultProfilePic" : "defaultProfilePicLight");
  const photo: string = user?.photo?.thumbnail || defaultProfilePic;

  const defaults: IUserSignin = useMemo(() => ({
    socialID: user?.socialID || "",
    email: user?.email || "",
    password: "",
  }), [user?.email, user?.socialID]);

  const {
    register,
    trigger,
    handleSubmit,
    reset,
    getValues,
    formState: {
      isDirty,
      isValid,
      errors
    }
  } = useForm<IUserSignin>({
    criteriaMode: "all",
    defaultValues: defaults
  });

  const userPolicy = _.get($store, `Data.userPolicy$.value.fields`, policies.userPolicy.fields);
  //endregion Variables

  //region Form Validations
  const validations: any = {
    socialID: {
      required: method === "id" ? "Social ID is required." : false,
      minLength: method === "id" ? {
        value: userPolicy.socialID.minLength,
        message: `Social ID should have at least ${userPolicy.socialID.minLength} characters.`,
      } : undefined,
      maxLength: method === "id" ? {
        value: userPolicy.socialID.maxLength,
        message: `Social ID can't exceed ${userPolicy.socialID.maxLength} characters.`,
      } : undefined,
      pattern: method === "id" ? {
        value: /^[a-zA-Z][a-zA-Z0-9-@_.]*$/,
        message: "Invalid characters entered.",
      } : undefined
    },
    email: {
      required: method === "email" ? "Email is required." : false,
      minLength: method === "email" ? {
        value: userPolicy.email.minLength,
        message: `Email should have at least ${userPolicy.email.minLength} characters.`,
      } : undefined,
      maxLength: method === "email" ? {
        value: userPolicy.email.maxLength,
        message: `Email can't exceed ${userPolicy.email.maxLength} characters.`,
      } : undefined,
      pattern: method === "email" ? {
        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        message: "Please enter a valid email address.",
      } : undefined
    },
    password: {
      required: "Password is required.",
      minLength: {
        value: userPolicy.password.minLength,
        message: `Password should have at least ${userPolicy.password.minLength} characters.`,
      }
    }
  }
  //endregion Form Validations

  //region Form Registrations
  const socialIDRef = useRef<HTMLInputElement | null>(null);
  const {
    ref: socialIDRegisterRef,
    onBlur: socialIDRegisterOnBlur,
    ...socialIDRegister
  } = register("socialID", validations.socialID);

  const emailRef = useRef<HTMLInputElement | null>(null);
  const {
    ref: emailRegisterRef,
    onBlur: emailRegisterOnBlur,
    ...emailRegister
  } = register("email", validations.email);

  const passwordRef = useRef<HTMLInputElement | null>(null);
  const {
    ref: passwordRegisterRef,
    onBlur: passwordRegisterOnBlur,
    ...passwordRegister
  } = register("password", validations.password);
  //endregion Form Registrations

  //region Methods & Event Handlers
  const {
    openDialog,
    closeDialog,
    setOnClose
  } = useDialogController();

  const {
    suppress,
    unsuppress
  } = useTippyRegistry();

  const setFocus = (id: boolean = false) => {
    const t = setTimeout(() => {
      if (!id && user) {
        passwordRef?.current?.focus();
      } else {
        if (method === "id") {
          socialIDRef?.current?.focus();
        } else {
          emailRef?.current?.focus();
        }
      }
      clearTimeout(t);
    }, 10)
  };

  const onValueChange = (value: string) => {
    setMethod(value);
    setFocus();
  }

  const onReset = () => {
    reset();
    const t = setTimeout(() => {
      if (method === "id") {
        socialIDRef.current?.focus();
        socialIDRef.current?.select();
      } else {
        emailRef.current?.focus();
        emailRef.current?.select();
      }
      clearTimeout(t);
    }, 10);
  };

  const onForgotPassword = async () => {
    suppress();
    const id = (method === 'id' ? getValues("socialID") : getValues("email"));
    if (_.isNothing(id)) {
      _.toastError("Please enter your social ID or email address.");
      unsuppress();
      return;
    }

    const payload = {
      params: {id: id}
    };

    setLoading(true);
    setLoadingLabel("Sending OTP...");
    if (!user) {
      let canProceed = true;
      const data = await $core.idExists(payload).catch((_err) => {
        setLoading(false);
        setLoadingLabel(null);
        _.toastError(`An unexpected error occurred while checking whether this account exists. Please try later or report if issue persists.`);
        canProceed = false;
      });

      if (!canProceed) {
        unsuppress();
        return;
      }

      if (!_.get(data, 'idExists', true)) {
        setLoading(false);
        setLoadingLabel(null);
        _.toastError(`There is no account registered with the ${(method === 'id' ? 'social ID' : 'email')} ${id}. Please sign up for a new account or enter an existing account.`);
        setFocus(true);
        unsuppress();
        return;
      }
    }

    await $core.sendChangePasswordOtp(id).then((user) => {
      setLoading(false);
      setLoadingLabel(null);
      const menuItems = _.find(_.get($store, 'App.menuItems$$', []), {id: 'userMenu'});
      const link = _.find(_.get(menuItems, 'links', []), {id: 'changePassword'});
      if (!link) {
        unsuppress();
        return;
      }

      const props = {
        ...link.properties,
        user,
        onSuccess: () => {
          const t = setTimeout(() => {
            _.toastSuccess(link.toastSuccessMessage);
            unsuppress();
            clearTimeout(t);
          }, 10);
        }
      };

      openDialog(ChangePassword, props);

    }).catch((e) => {
      setLoading(false);
      _.toastError(_.get(e, 'message'));
      unsuppress();
    });
  }

  const _onKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    e.stopPropagation();
    await _onSubmit();
  }

  const _onSubmit = async () => {
    if (!isValid) return;
    await handleSubmit(onSubmit)();
  }

  const onSubmit = async () => {
    setLoading(true);
    const payload: ISigninPayload = _.omitBy({
      socialID: getValues("socialID"),
      email: getValues("email"),
      hash: _.sha512(getValues("password")),
      device: {
        ...$store.Device.data || null,
        ...{deviceTime: new Date().toISOString()}
      }
    }, _.isNil) as ISigninPayload;

    await $core.signin(payload).then(async (data: IUserSigninResponse) => {
      setLoading(false);
      if (_.get(data, 'user.status') === UserStatus.active) {
        $store.User.user$.next(data.user);

        if (!_.isNothing(data, 'session.accessToken.token') && !_.isNothing(data, 'session.refreshToken.token')) {
          $store.User.session$.next(data.session);
          $e.$b($e.events.signedIn, $store.User.user);
          await closeDialog(true);
          onSuccess?.();
        }
      }

    }).catch((e: any) => {
      setLoading(false);
      _.toastError(_.get(e, 'message'));
      const t = setTimeout(() => {
        if (['accountNotFound', 'accountNotActive'].indexOf(_.get(e, 'code')) > -1) {
          if (method === "id") {
            socialIDRef.current?.focus();
            socialIDRef.current?.select();
          } else {
            emailRef.current?.focus();
            emailRef.current?.select();
          }

        } else {
          if (_.get(e, 'code') === 'incorrectPassword') {
            passwordRef.current?.focus();
            passwordRef.current?.select();
          } else {
            if (method === "id") {
              socialIDRef.current?.focus();
              socialIDRef.current?.select();
            } else {
              emailRef.current?.focus();
              emailRef.current?.select();
            }
          }
        }

        clearTimeout(t);
      }, 10)
    });
  };

  const onScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const distanceFromTop = el.scrollTop;

    const distanceFromBottom =
      el.scrollHeight - (el.scrollTop + el.clientHeight);

    // console.log({distanceFromTop, distanceFromBottom});

    const SHOW_THRESHOLD = 2;
    const HIDE_THRESHOLD = 15;
    const TOP_HIDE_THRESHOLD = 15;

    setIsAtBottom(prev => {
      // ✅ SHOW when near bottom
      if (!prev && distanceFromBottom <= SHOW_THRESHOLD) return true;

      // ✅ HIDE when near top (priority)
      if (distanceFromTop <= TOP_HIDE_THRESHOLD) return false;

      // ✅ HIDE when moving away from bottom
      if (prev && distanceFromBottom > HIDE_THRESHOLD) return false;

      return prev;
    });
  };

  const _onClose = useCallback(() => {
    onClose?.();
  }, [onClose]);
  //endregion Event Handlers

  //region Effects
  useEffect(() => {
    if (!footerRef.current) return;

    const el = footerRef.current;

    const updateHeight = () => {
      setFooterHeight(el.scrollHeight);
    };

    updateHeight();

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!contentRef.current) return;

    const el = contentRef.current;

    const checkOverflow = () => {
      requestAnimationFrame(() => {
        setIsOverflow(el.scrollHeight > el.clientHeight);
      });
    };

    checkOverflow();

    const observer = new ResizeObserver(() => checkOverflow());
    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    setFocus();
  }, []);

  useEffect(() => {
    setOnClose(_onClose);
  }, [setOnClose, _onClose]);
  //endregion Effects

  //region Toolbar Elements
  const headerElements: HeaderElement[] = [
    {
      name: "title",
      type: "span",
      position: "left",
      label: pages.signin.title,
      style: "text-md md:text-lg text-white font-bold tracking-wider"
    },
    {
      name: "reset",
      type: "button",
      position: "right",
      icon: "fa-rotate",
      tooltip: "Reset",
      style: "text-white scale-90 transition duration-200 hover:text-generic-hover hover:scale-120",
      hidden: !isDirty,
      onClick: () => onReset()
    },
    {
      name: "close",
      type: "button",
      position: "right",
      icon: "fa-square-xmark",
      tooltip: "Close",
      style: "text-white scale-115 transition duration-200 hover:text-light-red hover:scale-125",
      onClick: () => closeDialog()
    }
  ];

  const footerElements: FooterElement[] = [
    {
      name: "signin",
      type: "gradientButton",
      action: "submit",
      label: "Sign in",
      style: `btn-signin btn-${isValid ? 'blue transition duration-200 hover:text-generic-hover hover:scale-105' : 'gray'}`,
      disabled: !isValid,
      onClick: () => _onSubmit(),
      onEnter: () => _onSubmit()
    },
    {
      name: "cancel",
      type: "gradientButton",
      label: "Cancel",
      style: "btn-cancel btn-gray-reverse hover:text-light-red",
      onClick: () => closeDialog(),
    }
  ];
  //endregion Toolbar Elements

  //region HTML Elements
  const loadingSpinner = (
    <LoadingSpinner
      className="absolute inset-0 z-50 bg-bluegray-darker/85"
      borderRadius="rounded-sm md:rounded-md"
      label={loadingLabel}
    />
  );

  const toolbarTop = (
    <ToolbarTop
      name={`tbt-${className}`}
      elements={headerElements}
      leftToolbarGap="gap-1"
    />
  );

  const toolbarBottom = (
    <ToolbarBottom
      ref={footerRef}
      name={`tbb-${className}`}
      elements={footerElements}
    />
  );

  const signinForm = (
    <form
      onSubmit={_onSubmit}
      className={`form-${className} grid px-4 gap-4 text-sm md:text-md text-custom-gray-600 dark:text-pale-white leading-relaxed`}>
      {user && (
        <div
          className="w-full h-auto flex flex-col items-center text-center">
          <div
            className="profile-picture w-fit h-fit rounded-full overflow-hidden shadow-[1px_1px_2px_1px_rgba(105,120,127,1)]! hover:shadow-[1px_1px_2px_1px_rgba(55,90,105,1)]!">
            <img
              src={photo}
              alt={`${user.firstName} ${user.lastName}`}
              className="w-20 h-20 object-contain rounded-full border-none"
            />
          </div>
          <div
            className="font-semibold text-sm text-black dark:text-bluegray-lighter tracking-wide mt-2">
            {user?.firstName || user?.lastName ? `${user?.firstName} ${user?.lastName}` : ""}
          </div>
        </div>
      )}

      <div
        className="w-full h-auto flex flex-col gap-4">
        <div
          className="w-full flex flex-row gap-2 justify-center items-center self-center mb-1 px-1 py-0">
          <Label
            className="text-generic-dull">Sign
            in
            with</Label>
          <RadioGroup
            value={method}
            tabIndex={-1}
            orientation={"horizontal"}
            onValueChange={onValueChange}
            className="flex! flex-row gap-2 items-center">
            <Label
              htmlFor="radioId"
              className="group flex items-center gap-2 hover:cursor-pointer">
              <RadioGroupItem
                id="radioId"
                value="id"
                tabIndex={-1}
                className="h-2 w-2 flex items-center justify-center rounded-full border-2 transition-all duration-200 group-hover:cursor-pointer
                    border-generic-dull/60 bg-bluegray-darker hover:border-generic-dull hover:bg-bluegray-light/50 ring-2 ring-generic-dull/60
                    [&>span]:h-full [&>span]:w-full [&>span]:flex [&>span]:items-center [&>span]:justify-center [&>span_svg]:h-1 [&>span_svg]:w-1
                    data-[state=checked]:border-bluegray-light data-[state=checked]:bg-bluegray-light data-[state=checked]:ring-2 data-[state=checked]:ring-generic-hover/40"
              />
              <span
                className="text-generic-dull group-hover:cursor-pointer transition duration-200 group-hover:text-generic-hover group-hover:scale-105">Social ID</span>
            </Label>
            <Label
              htmlFor="radioEmail"
              className="group flex items-center gap-2 hover:cursor-pointer">
              <RadioGroupItem
                id="radioEmail"
                value="email"
                tabIndex={-1}
                className="h-2 w-2 flex items-center justify-center rounded-full border-2 transition-all duration-200 group-hover:cursor-pointer
                    border-generic-dull/60 bg-bluegray-darker hover:border-generic-dull hover:bg-bluegray-light/50 ring-2 ring-generic-dull/60
                    [&>span]:h-full [&>span]:w-full [&>span]:flex [&>span]:items-center [&>span]:justify-center [&>span_svg]:h-1 [&>span_svg]:w-1
                    data-[state=checked]:border-bluegray-light data-[state=checked]:bg-bluegray-light data-[state=checked]:ring-2 data-[state=checked]:ring-generic-hover/40"
              />
              <span
                className="transition duration-200 text-generic-dull group-hover:cursor-pointer group-hover:text-generic-hover group-hover:scale-105">Email</span>
            </Label>
          </RadioGroup>
        </div>

        <div
          className="w-full flex flex-col justify-center items-center self-center px-1 py-0">
          <InputValidator
            ref={useMergeRefs(socialIDRef, socialIDRegisterRef)}
            placeholder="Social ID*"
            readOnly={!!user?.socialID}
            minLength={userPolicy.socialID.minLength}
            maxLength={userPolicy.socialID.maxLength}
            mask={/[a-zA-Z0-9-@_.]/}
            display={method === "id"}
            disabled={method !== "id"}
            error={errors.socialID?.message}
            {...socialIDRegister}
            showToast={_.toastError}
            onBlur={async (e) => {
              await socialIDRegisterOnBlur(e);
              await trigger("socialID");
            }}
          />
          <InputValidator
            ref={useMergeRefs(emailRef, emailRegisterRef)}
            placeholder="Email*"
            readOnly={!!user?.email}
            minLength={userPolicy.email.minLength}
            maxLength={userPolicy.email.maxLength}
            mask={/[a-zA-Z0-9@._%+-]/}
            format="email"
            display={method === "email"}
            disabled={method !== "email"}
            error={errors.email?.message}
            {...emailRegister}
            showToast={_.toastError}
            onBlur={async (e) => {
              await emailRegisterOnBlur(e);
              await trigger("email");
            }}
          />
        </div>

        <div
          className="w-full flex flex-col justify-center items-center self-center px-1 py-0">
          <InputValidator
            ref={useMergeRefs(passwordRef, passwordRegisterRef)}
            type="password"
            placeholder="Password*"
            minLength={userPolicy.password.minLength}
            error={errors.password?.message}
            {...passwordRegister}
            showToast={_.toastError}
            onBlur={async (e) => {
              await passwordRegisterOnBlur(e);
              await trigger("password");
            }}
          />

          <button
            type="button"
            onClick={onForgotPassword}
            className="mt-2 transition duration-200 text-xs text-blue-600 underline cursor-pointer hover:text-generic-hover hover:scale-105"
          >
            Forgot
            Password
          </button>
        </div>
      </div>
    </form>
  );

  const content = (
    <div
      ref={contentRef}
      onScroll={onScroll}
      className={`
          content-${className}
          flex-1 py-4 min-h-0 box-border overflow-y-auto 
          bg-[radial-gradient(circle,#ffffff_0%,#d6d6d6_100%)] 
          dark:bg-[radial-gradient(circle,#030b11b8_0%,#030b11d8_100%)] 
          scrollbar-track-rounded-full scrollbar-thin 
          scrollbar-track-dim-white dark:scrollbar-track-transparent 
          scrollbar-thumb-custom-blue-600 
          scrollbar-hover:scrollbar-thumb-custom-blue-300 
          scrollbar-active:scrollbar-thumb-custom-blue-300
        `}
      style={{
        paddingBottom: isOverflow ? footerHeight + 16 : undefined
      }}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}>
      {signinForm}
    </div>
  );

  const staticFooter = (
    <div>
      {toolbarBottom}
    </div>
  );

  const fixedFooter = (
    <div
      className={`
        absolute bottom-0 left-0 right-0 z-50
        transition-transform duration-300 ease-in-out
        ${showFooter ? "translate-y-0" : "translate-y-full"}
      `}
    >
      {toolbarBottom}
    </div>
  );
  //endregion HTML Elements

  return (
    <div
      className="relative w-full h-full flex flex-col rounded-sm md:rounded-md overflow-hidden"
      onKeyDown={_onKeyDown}
      onWheel={(e) => e.stopPropagation()}>
      {loading && (
        loadingSpinner
      )}

      {toolbarTop}

      {content}

      {!isOverflow ? (
        staticFooter
      ) : (
        fixedFooter
      )}
    </div>
  );
}
